import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { EmptyState, MetricStrip } from "@/components/CommandSystem";
import { HoardexaLoader } from "@/components/HoardexaLoader";
import { RegisterSearch } from "@/components/RegisterSearch";
import { HoardingsMap } from "@/components/MapPicker";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { inr, fmtDate, CITIES } from "@/lib/format";
import { useHoardingTypes } from "@/lib/hoardingTypes";
import { BlockHoardingModal } from "./components/BlockHoardingModal";
import { UnblockHoardingModal } from "./components/UnblockHoardingModal";
import { MaintenanceModal } from "./components/MaintenanceModal";
import {
  Plus, Building2, CalendarDays,
  BookmarkPlus, RotateCcw, SlidersHorizontal, Image as ImageIcon, Map as MapIcon,
  Share2, Send, X, Ban, Wrench, CheckCircle2, Eye, Copy,
} from "lucide-react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PaginationFooter } from "@/components/PaginationFooter";
import { AvailabilityCalendar, addDays, dateKey } from "./components/AvailabilityCalendar";

const EMPTY_FILTERS = { city: "", status: "", type: "", insight: "", q: "" };

const PARTNER_LISTING_DEFAULTS = {
  printing_responsibility: "owner",
  mounting_responsibility: "owner",
  partner_visibility_status: "available",
};

const num = (v) => Number(String(v || "").replace(/[^\d.]/g, "") || 0);
const inputValue = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

function daysLeft(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function getPhotoUrl(item) {
  const photo = item?.photos?.[0] || item?.photo_metadata?.[0];
  if (typeof photo === "string") return photo;
  return photo?.url || item?.images?.[0] || "";
}

function partnerListingPath(id) {
  return `/hoardings/partner/${String(id || "").replace(/^partner:/, "")}`;
}

function defaultPartnerListingValues(item) {
  return {
    min_display_charge: inputValue(item?.monthly_price),
    min_printing_charge: inputValue(item?.default_printing_charge),
    min_mounting_charge: inputValue(item?.default_installation_charge ?? item?.default_mounting_charge),
  };
}

function fmtDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function HoardingThumb({ item }) {
  const url = getPhotoUrl(item);
  if (url) {
    return <img src={url} alt="" className="hx-hoarding-thumb" loading="lazy" />;
  }
  return (
    <span className="hx-hoarding-thumb is-empty" aria-hidden="true">
      <ImageIcon size={15} />
    </span>
  );
}

function HoardingStatusChip({ status, urgent, compact = false, blockedReason = "", blockedUntil = "" }) {
  const normalized = urgent ? "Expiring soon" : status || "Unknown";
  const title = normalized === "Blocked"
    ? [`Blocked${blockedUntil ? ` until ${fmtDateTime(blockedUntil)}` : ""}`, blockedReason].filter(Boolean).join(": ")
    : normalized;
  const tone = urgent
    ? "warning"
    : normalized === "Blocked"
      ? "danger"
      : normalized === "Occupied"
        ? "success"
        : normalized === "Available"
          ? "available"
          : normalized === "Maintenance"
            ? "danger"
            : "neutral";
  const shortLabel = urgent
    ? "Exp"
    : normalized === "Blocked"
      ? "Blk"
      : normalized === "Occupied"
        ? "Occ"
        : normalized === "Available"
          ? "Avail"
          : normalized === "Maintenance"
            ? "Maint"
            : normalized;
  return (
    <span className={`hx-hoarding-status tone-${tone} ${compact ? "is-compact" : ""}`} title={title}>
      <span />
      {compact ? shortLabel : normalized}
    </span>
  );
}

function ContextMenuItem({ icon: Icon, label, danger = false, onClick }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-semibold transition-colors ${danger ? "text-rose-700 hover:bg-rose-50" : "text-slate-700 hover:bg-slate-50"}`}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

export function Hoardings() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const applyingUrlState = useRef(false);
  const searchParamsKey = searchParams.toString();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loadingHoardings, setLoadingHoardings] = useState(true);
  const [hasLoadedHoardings, setHasLoadedHoardings] = useState(false);
  const [filters, setFilters] = useState(() => ({
    city: searchParams.get("city") || "",
    status: searchParams.get("status") || "",
    type: searchParams.get("type") || "",
    insight: searchParams.get("insight") || "",
    q: searchParams.get("q") || "",
  }));
  const [searchDraft, setSearchDraft] = useState(() => searchParams.get("q") || "");
  const appliedFilters = useMemo(() => ({
    city: filters.city,
    status: filters.status,
    type: filters.type,
    insight: filters.insight,
    q: filters.q,
  }), [
    filters.city,
    filters.status,
    filters.type,
    filters.insight,
    filters.q,
  ]);
  const [view, setView] = useState(() => {
    const initialView = searchParams.get("view");
    return initialView === "availability" || initialView === "map" ? initialView : "list";
  });
  const [availability, setAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [mapItems, setMapItems] = useState([]);
  const [loadingMap, setLoadingMap] = useState(false);
  const [error, setError] = useState("");
  const [availabilityError, setAvailabilityError] = useState("");
  const [mapError, setMapError] = useState("");
  const [selectedArea, setSelectedArea] = useState(() => searchParams.get("area") || "");
  const [availabilityFrom, setAvailabilityFrom] = useState(() => searchParams.get("from") || new Date().toISOString().slice(0, 10));
  const [availabilityDays, setAvailabilityDays] = useState(() => Number(searchParams.get("days")) || 14);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => Number(searchParams.get("rows")) || 10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sharingInventory, setSharingInventory] = useState(false);
  const [partnerSettings, setPartnerSettings] = useState(null);
  const [ownerPartnerships, setOwnerPartnerships] = useState([]);
  const [ownerListings, setOwnerListings] = useState([]);
  const [approvedPartnerInventoryCount, setApprovedPartnerInventoryCount] = useState(0);
  const [inventorySource, setInventorySource] = useState("owned");
  const [silentInventoryRefresh, setSilentInventoryRefresh] = useState(false);
  const [selectedHostIds, setSelectedHostIds] = useState([]);
  const [partnerListingForm, setPartnerListingForm] = useState(PARTNER_LISTING_DEFAULTS);
  const [partnerListingValues, setPartnerListingValues] = useState({});
  const [sharingBusy, setSharingBusy] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const nav = useNavigate();
  const { names: hoardingTypes } = useHoardingTypes();
  const canCreate = hasPermission(user, "create_hoardings");
  const canCreateCampaign = hasPermission(user, "create_campaigns");
  const canManagePartnerNetwork = hasPermission(user, "manage_partner_network");

  // Listen for Ctrl+K shortcut to focus search input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        const searchInput = document.querySelector('[data-testid="hoardings-search"]');
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Sync URL → state
  useEffect(() => {
    const urlParams = new URLSearchParams(searchParamsKey);
    const nextFilters = {
      city: urlParams.get("city") || "",
      status: urlParams.get("status") || "", type: urlParams.get("type") || "",
      insight: urlParams.get("insight") || "",
      q: urlParams.get("q") || "",
    };
    const urlView = urlParams.get("view");
    const nextView = urlView === "availability" || urlView === "map" ? urlView : "list";
    const nextFrom = urlParams.get("from") || new Date().toISOString().slice(0, 10);
    const requestedDays = Number(urlParams.get("days"));
    const nextDays = requestedDays >= 1 && requestedDays <= 90 ? requestedDays : 14;
    const requestedRows = Number(urlParams.get("rows"));
    const nextPageSize = requestedRows > 0 ? requestedRows : 10;

    applyingUrlState.current = true;
    setFilters((cur) => JSON.stringify(cur) === JSON.stringify(nextFilters) ? cur : nextFilters);
    setSearchDraft(nextFilters.q);
    setView(nextView);
    setSelectedArea(urlParams.get("area") || "");
    setAvailabilityFrom(nextFrom);
    setAvailabilityDays(nextDays);
    setPageSize(nextPageSize);
    setPage(1);
  }, [searchParamsKey]);

  // Sync state → URL
  useEffect(() => {
    if (applyingUrlState.current) { applyingUrlState.current = false; return; }
    const next = new URLSearchParams();
    const urlFilters = {
      city: filters.city,
      status: filters.status,
      type: filters.type,
      insight: filters.insight,
      q: filters.q,
    };
    Object.entries(urlFilters).forEach(([key, value]) => { if (value) next.set(key, value); });
    if (view !== "list") next.set("view", view);
    if (view === "map" && selectedArea) next.set("area", selectedArea);
    if (view === "availability") { next.set("from", availabilityFrom); next.set("days", String(availabilityDays)); }
    if (pageSize !== 10) next.set("rows", String(pageSize));
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [
    availabilityDays,
    availabilityFrom,
    filters.city,
    filters.insight,
    filters.q,
    filters.status,
    filters.type,
    pageSize,
    searchParams,
    selectedArea,
    setSearchParams,
    view,
  ]);

  const canViewPartnerInventory = canManagePartnerNetwork &&
    !!partnerSettings?.enabled &&
    !!partnerSettings?.use_partner_inventory_enabled &&
    approvedPartnerInventoryCount > 0;
  const viewingPartnerInventory = inventorySource === "partner" && canViewPartnerInventory;

  useEffect(() => {
    if (!canViewPartnerInventory && inventorySource === "partner") {
      setInventorySource("owned");
    }
  }, [canViewPartnerInventory, inventorySource]);

  useEffect(() => { setSelectedIds([]); }, [availabilityDays, availabilityFrom, appliedFilters, inventorySource, view]);

  // load() uses explicit pageNum to avoid React closure / dependency-loop issues.
  // Filter changes always call load(1) via the effect below; page navigation calls load(n) directly.
  const load = useCallback(async (pageNum) => {
    setLoadingHoardings(true);
    const offset = (pageNum - 1) * pageSize;
    const params = { limit: pageSize, offset };
    if (viewingPartnerInventory) params.inventory_source = "partner";
    Object.entries(appliedFilters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/hoardings", { params, skipGlobalLoader: true });
      if (data && !Array.isArray(data) && data.hoardings !== undefined) {
        setItems(data.hoardings);
        setTotal(data.total ?? 0);
        setSummary(data.summary || null);
      } else {
        // Fallback for any unexpected shape
        setItems(Array.isArray(data) ? data : []);
        setTotal(0);
        setSummary(null);
      }
      setError("");
      setPage(pageNum);
    } catch (e) {
      if (e.response?.status === 423) {
        setError("Your data migration is in progress. Hoardings will be available after import completes.");
        return;
      }
      setError(formatApiError(e.response?.data?.detail) || "Hoardings could not be loaded.");
    } finally {
      setLoadingHoardings(false);
      setSilentInventoryRefresh(false);
      setHasLoadedHoardings(true);
    }
  }, [appliedFilters, pageSize, viewingPartnerInventory]);

  // Re-fetch from page 1 whenever committed filters change.
  useEffect(() => { load(1); }, [load]);

  const loadAvailability = useCallback(async () => {
    setLoadingAvailability(true);
    const params = { from: availabilityFrom, days: availabilityDays };
    if (viewingPartnerInventory) params.inventory_source = "partner";
    Object.entries(appliedFilters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/hoardings/availability", { params, skipGlobalLoader: true });
      setAvailability(data);
      setAvailabilityError("");
    } catch (e) {
      if (e.response?.status === 423) { setAvailabilityError("Your data migration is in progress."); return; }
      setAvailabilityError(formatApiError(e.response?.data?.detail) || "Availability could not be loaded.");
    } finally {
      setLoadingAvailability(false);
    }
  }, [availabilityDays, availabilityFrom, appliedFilters, viewingPartnerInventory]);

  useEffect(() => {
    if (view === "availability") loadAvailability();
  }, [loadAvailability, view]);

  useEffect(() => {
    if (!canManagePartnerNetwork) return;
    api.get("/partner-network/settings", { skipGlobalLoader: true })
      .then((settingsRes) => {
        const nextSettings = settingsRes.data || {};
        setPartnerSettings(nextSettings);
        setApprovedPartnerInventoryCount(Number(nextSettings.taken_inventory_count || 0));
      })
      .catch(() => {
        setPartnerSettings(null);
        setApprovedPartnerInventoryCount(0);
      });

    api.get("/partner-network/listings?side=host", { skipGlobalLoader: true })
      .then((hostListingsRes) => {
        const hostListings = Array.isArray(hostListingsRes.data) ? hostListingsRes.data : [];
        setApprovedPartnerInventoryCount(hostListings.filter((l) => l.status === "approved" && l.partner_visibility_status === "available").length);
      })
      .catch(() => {});

    api.get("/partner-network/partnerships?side=owner", { skipGlobalLoader: true })
      .then((partnershipsRes) => setOwnerPartnerships(Array.isArray(partnershipsRes.data) ? partnershipsRes.data : []))
      .catch(() => setOwnerPartnerships([]));

    api.get("/partner-network/listings?side=owner", { skipGlobalLoader: true })
      .then((ownerListingsRes) => setOwnerListings(Array.isArray(ownerListingsRes.data) ? ownerListingsRes.data : []))
      .catch(() => setOwnerListings([]));
  }, [canManagePartnerNetwork]);

  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    const onKeyDown = (event) => { if (event.key === "Escape") close(); };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [contextMenu]);

  const loadMapItems = useCallback(async () => {
    setLoadingMap(true);
    const params = {};
    if (viewingPartnerInventory) params.inventory_source = "partner";
    Object.entries(appliedFilters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/hoardings", { params, skipGlobalLoader: true });
      const nextItems = Array.isArray(data) ? data : data?.hoardings || [];
      setMapItems(nextItems);
      setMapError("");
    } catch (e) {
      if (e.response?.status === 423) { setMapError("Your data migration is in progress."); return; }
      setMapError(formatApiError(e.response?.data?.detail) || "Map data could not be loaded.");
    } finally {
      setLoadingMap(false);
    }
  }, [appliedFilters, viewingPartnerInventory]);

  useEffect(() => {
    if (view === "map") loadMapItems();
  }, [loadMapItems, view]);

  // Backend returns exactly one page of sorted results; no client-side sort or slice needed
  const pageItems = items;

  function updateSearch(value) {
    setSearchDraft(value.slice(0, 50));
  }

  function commitSearch() {
    const nextQuery = searchDraft.trim();
    setFilters((cur) => cur.q === nextQuery ? cur : { ...cur, q: nextQuery });
    setPage(1);
  }

  function updateCityFilter(value) {
    setSelectedArea("");
    setFilters((cur) => ({ ...cur, city: value === "all" ? "" : value }));
  }

  function resetView() { setFilters(EMPTY_FILTERS); setSearchDraft(""); setSelectedArea(""); setView("list"); setPage(1); setPageSize(10); }

  function startCampaignFromAvailability() {
    if (selectedIds.length === 0) return;
    nav("/campaigns", {
      state: {
        createCampaign: {
          hoarding_ids: selectedIds,
          start_date: availabilityFrom,
          end_date: dateKey(addDays(new Date(`${availabilityFrom}T00:00:00`), availabilityDays - 1)),
        },
      },
    });
  }

  function toggleSelected(id, checked, item) {
    setSelectedIds((cur) => checked ? [...new Set([...cur, id])] : cur.filter((item) => item !== id));
    setPartnerListingValues((cur) => {
      if (checked) {
        return cur[id] ? cur : { ...cur, [id]: defaultPartnerListingValues(item) };
      }
      const next = { ...cur };
      delete next[id];
      return next;
    });
  }

  function updatePartnerListingValue(id, field, value) {
    setPartnerListingValues((cur) => ({
      ...cur,
      [id]: {
        ...defaultPartnerListingValues(items.find((item) => item.id === id)),
        ...(cur[id] || {}),
        [field]: value,
      },
    }));
  }

  function partnerValuesFor(id) {
    return partnerListingValues[id] || defaultPartnerListingValues(items.find((item) => item.id === id));
  }

  function addSelectedHost(id) {
    if (!id || id === "none") return;
    setSelectedHostIds((cur) => cur.includes(id) ? cur : [...cur, id]);
  }

  function removeSelectedHost(id) {
    setSelectedHostIds((cur) => cur.filter((item) => item !== id));
  }

  const isSharedWithHost = useCallback((hoardingID, partnershipID) => {
    return ownerListings.some((listing) =>
      listing.partnership_id === partnershipID &&
      listing.hoarding_id === hoardingID &&
      ["offered", "approved"].includes(listing.status)
    );
  }, [ownerListings]);

  const isSharedWithAllSelectedHosts = useCallback((hoardingID) => {
    return selectedHostIds.length > 0 && selectedHostIds.every((partnershipID) => isSharedWithHost(hoardingID, partnershipID));
  }, [isSharedWithHost, selectedHostIds]);

  async function shareSelectedInventory() {
    if (selectedIds.length === 0) {
      toast.error("Select at least one hoarding to share");
      return;
    }
    if (selectedHostIds.length === 0) {
      toast.error("Choose at least one host advertiser");
      return;
    }
    setSharingBusy(true);
    try {
      const requests = selectedHostIds.map((partnershipID) => {
        const newIds = selectedIds.filter((hoardingID) => !isSharedWithHost(hoardingID, partnershipID));
        if (newIds.length === 0) return null;
        return api.post("/partner-network/offer", {
          partnership_id: partnershipID,
          listings: newIds.map((hoarding_id) => ({
            hoarding_id,
            ...partnerListingForm,
            min_display_charge: num(partnerValuesFor(hoarding_id).min_display_charge),
            min_printing_charge: num(partnerValuesFor(hoarding_id).min_printing_charge),
            min_mounting_charge: num(partnerValuesFor(hoarding_id).min_mounting_charge),
          })),
        });
      }).filter(Boolean);
      if (requests.length === 0) {
        toast.error("Selected inventory is already shared with selected host advertiser(s)");
        return;
      }
      await Promise.all(requests);
      toast.success("Selected inventory shared for host approval");
      setSelectedIds([]);
      setPartnerListingValues({});
      await load(page);
      const { data } = await api.get("/partner-network/listings?side=owner", { skipGlobalLoader: true });
      setOwnerListings(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSharingBusy(false);
    }
  }

  function openRowMenu(event, item) {
    if (viewingPartnerInventory || sharingInventory) return;
    event.preventDefault();
    setContextMenu({
      item,
      x: Math.min(event.clientX, window.innerWidth - 230),
      y: Math.min(event.clientY, window.innerHeight - 260),
    });
  }

  async function refreshCurrentPage() {
    setSilentInventoryRefresh(true);
    await load(page);
    if (view === "map") loadMapItems();
    if (view === "availability") loadAvailability();
  }

  async function updateHoardingStatus(item, action) {
    setContextMenu(null);
    try {
      if (action === "unblock") {
        await api.post(`/hoardings/${item.id}/unblock`);
        toast.success("Hoarding unblocked");
      } else if (action === "available") {
        await api.post(`/hoardings/${item.id}/mark-available`);
        toast.success("Hoarding marked available");
      }
      await refreshCurrentPage();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update hoarding");
    }
  }

  async function saveContextUnblock() {
    const item = actionModal?.item;
    if (!item) return;
    try {
      await api.post(`/hoardings/${item.id}/unblock`);
      toast.success("Hoarding unblocked");
      setActionModal(null);
      await refreshCurrentPage();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to unblock hoarding");
      throw e;
    }
  }

  async function saveContextBlock(payload) {
    const item = actionModal?.item;
    if (!item) return;
    try {
      await api.post(`/hoardings/${item.id}/block`, payload);
      toast.success("Hoarding blocked");
      setActionModal(null);
      await refreshCurrentPage();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to block hoarding");
      throw e;
    }
  }

  async function saveContextMaintenance(payload) {
    const item = actionModal?.item;
    if (!item) return;
    try {
      await api.post(`/hoardings/${item.id}/schedule`, payload);
      toast.success("Maintenance scheduled");
      setActionModal(null);
      await refreshCurrentPage();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to schedule maintenance");
      throw e;
    }
  }

  async function copyHoardingId(item) {
    setContextMenu(null);
    try {
      await navigator.clipboard.writeText(item.hoarding_id || item.id);
      toast.success("Hoarding ID copied");
    } catch {
      toast.error("Could not copy hoarding ID");
    }
  }

  const pageAvailable = items.filter((item) => item.occupancy_status === "Available").length;
  const pageOccupied = items.filter((item) => item.occupancy_status === "Occupied").length;
  const pageBlocked = items.filter((item) => item.occupancy_status === "Blocked").length;
  const pageMaintenance = items.filter((item) => item.occupancy_status === "Maintenance").length;
  const pageExpiringSoon = items.filter((item) => {
    const left = daysLeft(item.campaign_end);
    return left !== null && left >= 0 && left <= 7;
  }).length;
  const inventorySummary = summary || {
    total: total || items.length,
    available: pageAvailable,
    occupied: pageOccupied,
    blocked: pageBlocked,
    maintenance: pageMaintenance,
    expiring_soon: pageExpiringSoon,
  };
  const canShareInventory = !viewingPartnerInventory && canManagePartnerNetwork && !!partnerSettings?.enabled && !!partnerSettings?.share_inventory_enabled;
  const ownerPartnerOptions = ownerPartnerships.filter((p) => p.status === "active");
  const selectedHosts = ownerPartnerOptions.filter((p) => selectedHostIds.includes(p.id));

  useEffect(() => {
    if (!sharingInventory || selectedHostIds.length === 0 || selectedIds.length === 0) return;
    setSelectedIds((cur) => cur.filter((id) => !isSharedWithAllSelectedHosts(id)));
  }, [isSharedWithAllSelectedHosts, selectedHostIds.length, selectedIds.length, sharingInventory]);

  useEffect(() => {
    setPartnerListingValues((cur) => Object.fromEntries(Object.entries(cur).filter(([id]) => selectedIds.includes(id))));
  }, [selectedIds]);

  const PartnerInventorySwitch = () => {
    if (!canViewPartnerInventory) return null;
    return (
      <button
        type="button"
        className={`hx-partner-inventory-switch ${viewingPartnerInventory ? "is-active" : ""}`}
        onClick={() => {
          setSilentInventoryRefresh(true);
          setInventorySource((current) => current === "partner" ? "owned" : "partner");
          setPage(1);
          setSelectedIds([]);
          setPartnerListingValues({});
          setSharingInventory(false);
        }}
        aria-pressed={viewingPartnerInventory}
      >
        <span className="hx-partner-switch-track" aria-hidden="true"><span /></span>
        <span>Partner n/w</span>
      </button>
    );
  };
  const ShareInventoryToggle = () => (
    <label className="hx-share-inventory-toggle">
      <input
        type="checkbox"
        className="hx-share-checkbox"
        checked={sharingInventory}
        onChange={(e) => {
          setSharingInventory(e.target.checked);
          if (!e.target.checked) {
            setSelectedIds([]);
            setPartnerListingValues({});
          }
        }}
      />
      <span className="hx-share-checkbox-box" aria-hidden="true" />
      <span className="hx-share-inventory-label">
        <Share2 size={14} /> Share inventory
      </span>
    </label>
  );

  const renderSharePriceInputs = (h) => {
    const values = partnerValuesFor(h.id);
    return (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Input
          type="number"
          min="0"
          placeholder="Display/month"
          value={values.min_display_charge}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => updatePartnerListingValue(h.id, "min_display_charge", e.target.value)}
        />
        <Input
          type="number"
          min="0"
          placeholder="Printing"
          value={values.min_printing_charge}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => updatePartnerListingValue(h.id, "min_printing_charge", e.target.value)}
        />
        <Input
          type="number"
          min="0"
          placeholder="Mounting"
          value={values.min_mounting_charge}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => updatePartnerListingValue(h.id, "min_mounting_charge", e.target.value)}
        />
      </div>
    );
  };

  const cityOptions = useMemo(() => {
    const values = new Set(CITIES);
    mapItems.forEach((item) => { if (item.city) values.add(item.city); });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [mapItems]);
  const areaOptions = useMemo(() => {
    const values = new Set();
    mapItems.forEach((item) => {
      if ((!filters.city || item.city === filters.city) && item.area) values.add(item.area);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [filters.city, mapItems]);
  const mapSourceItems = useMemo(() => mapItems.filter((item) => {
    if (filters.city && item.city !== filters.city) return false;
    if (selectedArea && item.area !== selectedArea) return false;
    return true;
  }), [filters.city, mapItems, selectedArea]);
  const mapMarkers = useMemo(() => mapSourceItems
    .map((item) => {
      const lat = Number(item.latitude);
      const lng = Number(item.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return {
        id: item.id,
        lat,
        lng,
        name: item.name || item.hoarding_id || "Hoarding",
        hoardingId: item.hoarding_id,
        city: item.city,
        area: item.area,
        status: item.occupancy_status,
        price: item.monthly_price ? inr(item.monthly_price) : "",
      };
    })
    .filter(Boolean), [mapSourceItems]);
  const areaHighlightMarkers = useMemo(() => {
    if (!selectedArea) return [];
    return mapItems
      .filter((item) => item.area === selectedArea && (!filters.city || item.city === filters.city))
      .map((item) => ({ lat: Number(item.latitude), lng: Number(item.longitude) }))
      .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
  }, [filters.city, mapItems, selectedArea]);
  const cityCounts = useMemo(() => mapItems.reduce((acc, item) => {
    const city = item.city || "Unassigned";
    acc[city] = (acc[city] || 0) + 1;
    return acc;
  }, {}), [mapItems]);
  const areaCounts = useMemo(() => mapItems.reduce((acc, item) => {
    if (filters.city && item.city !== filters.city) return acc;
    const area = item.area || "Unassigned";
    acc[area] = (acc[area] || 0) + 1;
    return acc;
  }, {}), [filters.city, mapItems]);
  const locatedMapCount = mapMarkers.length;

  return (
    <Layout
      title="Hoardings"
      subtitle={view === "availability" ? "Availability calendar for sellable inventory" : view === "map" ? "City map with area highlighting" : total > 0 ? `${total} hoardings across your network` : "Inventory workspace"}
      actions={
        <div className="flex items-center gap-2.5">
          {canCreate && (
            <button onClick={() => nav("/hoardings/new")} data-testid="add-hoarding-btn" className="hx-primary-action">
              <Plus size={16} strokeWidth={2.5} />
              <span className="hidden sm:inline">Add New Hoarding</span>
              <span className="sm:hidden">Add</span>
            </button>
          )}
        </div>
      }
    >
      {error && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-900">{error}</div>
      )}

      {loadingHoardings && !hasLoadedHoardings && !error ? (
        <HoardexaLoader label="Loading hoardings" />
      ) : (
        <div className="hx-hoardings-page">
          <MetricStrip
            metrics={[
              { label: "Total Inventory", value: inventorySummary.total, icon: Building2, tone: "indigo" },
              view === "availability" && selectedIds.length > 0 && { label: "Selected", value: selectedIds.length, icon: BookmarkPlus, tone: "emerald" },
              { label: "Available", value: inventorySummary.available, icon: Building2, tone: "blue" },
              { label: "Blocked", value: inventorySummary.blocked, icon: Ban, tone: "rose" },
              { label: "Occupied", value: inventorySummary.occupied, icon: CalendarDays, tone: "emerald" },
              { label: "Expiring Soon", value: inventorySummary.expiring_soon, icon: SlidersHorizontal, tone: "amber" },
              { label: "Maintenance", value: inventorySummary.maintenance, icon: SlidersHorizontal, tone: "rose" },
            ].filter(Boolean)}
          />

      {/* Content */}
      {view === "availability" ? (
        availabilityError ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-900">{availabilityError}</div>
        ) : (
          <AvailabilityCalendar
            data={availability}
            loading={loadingAvailability}
            days={availabilityDays}
            pageSize={pageSize}
            onOpen={(id) => nav(`/hoardings/${id}`)}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            canCreateCampaign={canCreateCampaign}
            onStartCampaign={startCampaignFromAvailability}
            controls={(
              <div className="hx-register-controls">
                <RegisterSearch
                  value={searchDraft}
                  onChange={updateSearch}
                  onSearch={commitSearch}
                  placeholder="Search Hoardings..."
                  testId="hoardings-search"
                  ariaLabel="Search hoardings"
                />
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); }}>
                  <SelectTrigger data-testid="page-size" className="hx-register-rows">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="25">25 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            filterbar={(
              <div className="hx-register-filterbar">
                <div className="hx-view-toggle">
                  <button type="button" onClick={() => setView("list")} className={view === "list" ? "is-active" : ""}>
                    <Building2 size={15} /> List
                  </button>
                  <button type="button" onClick={() => setView("availability")} className={view === "availability" ? "is-active" : ""}>
                    <CalendarDays size={15} /> Availability
                  </button>
                  <button type="button" onClick={() => setView("map")} className={view === "map" ? "is-active" : ""}>
                    <MapIcon size={15} /> Map
                  </button>
                </div>

                <div className="hx-register-filter">
                  <span>City</span>
                  <Select value={filters.city || "all"} onValueChange={updateCityFilter}>
                    <SelectTrigger data-testid="filter-city">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="hx-register-filter">
                  <span>Status</span>
                  <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="Blocked">Blocked</SelectItem>
                        <SelectItem value="Occupied">Occupied</SelectItem>
                      <SelectItem value="Available">Available</SelectItem>
                      <SelectItem value="Maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="hx-register-filter">
                  <span>Insight</span>
                  <div className="hx-insight-filter-row">
                    <Select value={filters.insight || "all"} onValueChange={(v) => setFilters({ ...filters, insight: v === "all" ? "" : v })}>
                      <SelectTrigger data-testid="filter-insight">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Hoardings</SelectItem>
                        <SelectItem value="most_profitable">Most Profitable</SelectItem>
                        <SelectItem value="loss_making">Loss-making</SelectItem>
                        <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                      </SelectContent>
                    </Select>
                    {canShareInventory && <ShareInventoryToggle />}
                  </div>
                </div>

                <label className="hx-register-filter">
                  <span>From</span>
                  <input type="date" value={availabilityFrom} onChange={(e) => setAvailabilityFrom(e.target.value)} />
                </label>

                <label className="hx-register-filter">
                  <span>Window</span>
                  <Select value={String(availabilityDays)} onValueChange={(v) => setAvailabilityDays(Number(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[7, 14, 30, 60, 90].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
                    </SelectContent>
                  </Select>
                </label>

                <button type="button" onClick={resetView} className="hx-reset-action">
                  <RotateCcw size={13} /> Reset
                </button>
              </div>
            )}
            hoardingHeader={(
              <Select value={filters.type || "all"} onValueChange={(v) => setFilters({ ...filters, type: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-type" className="hx-table-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {hoardingTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  {hoardingTypes.length === 0 && <SelectItem value="__no_types" disabled>No types yet</SelectItem>}
                </SelectContent>
              </Select>
            )}
          />
        )
      ) : view === "map" ? (
        <section className="hx-inventory-register hx-map-workspace" aria-busy={loadingMap}>
          <header>
            <div>
              <h2>Overall Map</h2>
              <p>
                {locatedMapCount} mapped hoarding{locatedMapCount === 1 ? "" : "s"}
                {filters.city ? ` in ${filters.city}` : " across selected cities"}
                {selectedArea ? ` · ${selectedArea}` : ""}
              </p>
            </div>
            <div className="hx-register-controls">
              <RegisterSearch
                value={searchDraft}
                onChange={updateSearch}
                onSearch={commitSearch}
                placeholder="Search Hoardings..."
                testId="hoardings-search"
                ariaLabel="Search hoardings"
              />
              {loadingMap && (
                <span className="hx-register-refresh" role="status">
                  <span aria-hidden="true" />
                  Updating
                </span>
              )}
            </div>
          </header>

          <div className="hx-register-filterbar">
            <div className="hx-view-toggle">
              <button type="button" onClick={() => setView("list")} className={view === "list" ? "is-active" : ""}>
                <Building2 size={15} /> List
              </button>
              <button type="button" onClick={() => setView("availability")} className={view === "availability" ? "is-active" : ""}>
                <CalendarDays size={15} /> Availability
              </button>
              <button type="button" onClick={() => setView("map")} className={view === "map" ? "is-active" : ""}>
                <MapIcon size={15} /> Map
              </button>
            </div>

            <label className="hx-register-filter">
              <span>City</span>
              <Select value={filters.city || "all"} onValueChange={updateCityFilter}>
                <SelectTrigger data-testid="filter-city">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>

            <label className="hx-register-filter">
              <span>Area</span>
              <Select value={selectedArea || "all"} onValueChange={(v) => setSelectedArea(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-area">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Areas</SelectItem>
                  {areaOptions.map((area) => <SelectItem key={area} value={area}>{area}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>

            <label className="hx-register-filter">
              <span>Status</span>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                  <SelectItem value="Occupied">Occupied</SelectItem>
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </label>

            <label className="hx-register-filter">
              <span>Type</span>
              <Select value={filters.type || "all"} onValueChange={(v) => setFilters({ ...filters, type: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {hoardingTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  {hoardingTypes.length === 0 && <SelectItem value="__no_types" disabled>No types yet</SelectItem>}
                </SelectContent>
              </Select>
            </label>

            <button type="button" onClick={resetView} className="hx-reset-action">
              <RotateCcw size={13} /> Reset
            </button>
          </div>

          {mapError ? (
            <div className="m-4 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-900">{mapError}</div>
          ) : (
            <div className={`hx-map-grid ${loadingMap ? "is-refreshing" : ""}`}>
              <aside className="hx-map-sidepanel">
                <div>
                  <h3>Cities</h3>
                  <div className="hx-map-chip-list">
                    <button type="button" className={!filters.city ? "is-active" : ""} onClick={() => updateCityFilter("all")}>
                      <span>All Cities</span>
                      <strong>{mapItems.length}</strong>
                    </button>
                    {Object.entries(cityCounts).map(([city, count]) => (
                      <button type="button" key={city} className={filters.city === city ? "is-active" : ""} onClick={() => updateCityFilter(city)}>
                        <span>{city}</span>
                        <strong>{count}</strong>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3>Areas</h3>
                  <div className="hx-map-chip-list">
                    <button type="button" className={!selectedArea ? "is-active" : ""} onClick={() => setSelectedArea("")}>
                      <span>All Areas</span>
                      <strong>{Object.values(areaCounts).reduce((sum, count) => sum + count, 0)}</strong>
                    </button>
                    {Object.entries(areaCounts).map(([area, count]) => (
                      <button type="button" key={area} className={selectedArea === area ? "is-active is-area" : ""} onClick={() => setSelectedArea(area === "Unassigned" ? "" : area)}>
                        <span>{area}</span>
                        <strong>{count}</strong>
                      </button>
                    ))}
                  </div>
                </div>
              </aside>

              <div className="hx-map-panel">
                {locatedMapCount > 0 ? (
                  <HoardingsMap
                    markers={mapMarkers}
                    areaHighlightMarkers={areaHighlightMarkers}
                    height={620}
                    onOpen={(marker) => {
                      if (viewingPartnerInventory) {
                        nav(partnerListingPath(marker.id));
                        return;
                      }
                      nav(`/hoardings/${marker.id}`);
                    }}
                  />
                ) : (
                  <EmptyState title="No mapped hoardings found" description="Add latitude and longitude to hoardings or adjust the selected city and area." />
                )}
              </div>
            </div>
          )}
        </section>
      ) : (
        <section className="hx-inventory-register" aria-busy={loadingHoardings}>
          <header>
            <div>
              <h2>Inventory Register</h2>
              <p>{total || pageItems.length} records in current view</p>
            </div>
            <PartnerInventorySwitch />
            <div className="hx-register-controls">
              <RegisterSearch
                value={searchDraft}
                onChange={updateSearch}
                onSearch={commitSearch}
                placeholder="Search Hoardings..."
                testId="hoardings-search"
                ariaLabel="Search hoardings"
              />
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); }}>
                <SelectTrigger data-testid="page-size" className="hx-register-rows">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 per page</SelectItem>
                  <SelectItem value="25">25 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                </SelectContent>
              </Select>
              {loadingHoardings && !silentInventoryRefresh && (
                <span className="hx-register-refresh" role="status">
                  <span aria-hidden="true" />
                  Updating
                </span>
              )}
            </div>
          </header>

          <div className="hx-register-filterbar">
            <div className="hx-view-toggle">
              <button type="button" onClick={() => setView("list")} className={view === "list" ? "is-active" : ""}>
                <Building2 size={15} /> List
              </button>
              <button type="button" onClick={() => setView("availability")} className={view === "availability" ? "is-active" : ""}>
                <CalendarDays size={15} /> Availability
              </button>
              <button type="button" onClick={() => setView("map")} className={view === "map" ? "is-active" : ""}>
                <MapIcon size={15} /> Map
              </button>
            </div>

            <div className="hx-register-filter">
              <span>City</span>
              <Select value={filters.city || "all"} onValueChange={updateCityFilter}>
                <SelectTrigger data-testid="filter-city">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="hx-register-filter">
              <span>Insight</span>
              <div className="hx-insight-filter-row">
                <Select value={filters.insight || "all"} onValueChange={(v) => setFilters({ ...filters, insight: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="filter-insight">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Hoardings</SelectItem>
                    <SelectItem value="most_profitable">Most Profitable</SelectItem>
                    <SelectItem value="loss_making">Loss-making</SelectItem>
                    <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                  </SelectContent>
                </Select>
                {canShareInventory && <ShareInventoryToggle />}
              </div>
            </div>

            <button type="button" onClick={resetView} className="hx-reset-action">
              <RotateCcw size={13} /> Reset
            </button>
          </div>

          {canShareInventory && sharingInventory && (
            <section className="hx-share-inventory-panel">
              <div className="hx-share-inventory-summary">
                {selectedIds.length} selected for {selectedHostIds.length} host advertiser{selectedHostIds.length === 1 ? "" : "s"}
              </div>

              <div className="mt-4 grid gap-3 xl:grid-cols-[1.2fr_1fr_1fr_1fr]">
                <label className="space-y-1 xl:col-span-4">
                    <span className="text-xs font-bold text-slate-600">Host</span>
                    <Select value="none" onValueChange={addSelectedHost}>
                      <SelectTrigger><SelectValue placeholder="Choose host" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Choose host</SelectItem>
                        {ownerPartnerOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.host_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </label>
                  {selectedHosts.length > 0 && (
                    <div className="flex flex-wrap gap-2 xl:col-span-4">
                      {selectedHosts.map((p) => (
                        <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200">
                          {p.host_name}
                          <button type="button" onClick={() => removeSelectedHost(p.id)} className="rounded-full p-0.5 hover:bg-emerald-100" aria-label={`Remove ${p.host_name}`}>
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <Select value={partnerListingForm.partner_visibility_status} onValueChange={(v) => setPartnerListingForm((p) => ({ ...p, partner_visibility_status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available to partners</SelectItem>
                      <SelectItem value="hidden">Hidden</SelectItem>
                      <SelectItem value="occupied">Occupied for partners</SelectItem>
                      <SelectItem value="maintenance">Maintenance for partners</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={partnerListingForm.printing_responsibility} onValueChange={(v) => setPartnerListingForm((p) => ({ ...p, printing_responsibility: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="owner">Printing by owner</SelectItem><SelectItem value="host">Printing by host</SelectItem></SelectContent>
                  </Select>
                  <Select value={partnerListingForm.mounting_responsibility} onValueChange={(v) => setPartnerListingForm((p) => ({ ...p, mounting_responsibility: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="owner">Mounting by owner</SelectItem><SelectItem value="host">Mounting by host</SelectItem></SelectContent>
                  </Select>
                  <div className="flex items-end xl:col-span-2">
                    <button type="button" onClick={shareSelectedInventory} disabled={sharingBusy} className="hx-primary-action">
                      <Send size={15} /> {sharingBusy ? "Sharing..." : "Share"}
                    </button>
                  </div>
                </div>
            </section>
          )}

          <div className={`hx-mobile-hoarding-list md:hidden ${loadingHoardings ? "is-refreshing" : ""}`}>
            {pageItems.map((h) => {
              const dl = daysLeft(h.campaign_end);
              const isEnded = dl !== null && dl < 0;
              const isUrgent = dl !== null && dl >= 0 && dl <= 7;
              const shareDisabled = sharingInventory && isSharedWithAllSelectedHosts(h.id);
              return (
                <button
                  type="button"
                  key={h.id}
                  data-testid={`hoarding-row-${h.id}`}
                  onClick={() => nav(viewingPartnerInventory ? partnerListingPath(h.id) : `/hoardings/${h.id}`)}
                  onContextMenu={(e) => openRowMenu(e, h)}
                  className="hx-mobile-hoarding-card"
                >
                  <div className="flex items-start gap-3">
                    {sharingInventory && (
                      <input
                        type="checkbox"
                        className="hx-row-share-checkbox mt-2"
                        checked={selectedIds.includes(h.id)}
                        disabled={shareDisabled}
                        title={shareDisabled ? "Already shared with selected host" : undefined}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => toggleSelected(h.id, e.target.checked, h)}
                      />
                    )}
                    <HoardingThumb item={h} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 title={h.name}>{h.name}</h3>
                          <p>
                            <span>{h.hoarding_id}</span>
                            {(h.area || h.city) && <> · {[h.area, h.city].filter(Boolean).join(", ")}</>}
                          </p>
                        </div>
                        <HoardingStatusChip status={h.occupancy_status} urgent={isUrgent} compact blockedReason={h.blocked_reason} blockedUntil={h.blocked_until} />
                      </div>
                    </div>
                  </div>
                  <div className="hx-mobile-hoarding-meta">
                    <div>
                      <span>End / Availability</span>
                      {dl !== null ? (
                        <>
                          <strong>{fmtDate(h.campaign_end)}</strong>
                          <small className={isEnded ? "is-danger" : isUrgent ? "is-warning" : ""}>
                            {isEnded ? `Available now · ended ${Math.abs(dl)} day${Math.abs(dl) === 1 ? "" : "s"} ago` : `${dl} day${dl === 1 ? "" : "s"} left`}
                          </small>
                        </>
                      ) : (
                        <strong className="is-available">Available</strong>
                      )}
                    </div>
                    <div>
                      <span>Commercial</span>
                      <strong>{h.monthly_price ? inr(h.monthly_price) : "—"}</strong>
                      <small>{[h.type, h.width && h.height ? `${h.width} x ${h.height}` : ""].filter(Boolean).join(" · ") || "—"}</small>
                    </div>
                  </div>
                  {h.client_name && <p className="hx-mobile-client">Client: {h.client_name}</p>}
                  {sharingInventory && selectedIds.includes(h.id) && (
                    <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                      {renderSharePriceInputs(h)}
                    </div>
                  )}
                </button>
              );
            })}
            {pageItems.length === 0 && (
              <EmptyState title="No hoardings found" description="Try adjusting your filters or search query." />
            )}
          </div>

          <div className={`hx-inventory-table-wrap hidden md:block ${loadingHoardings ? "is-refreshing" : ""}`}>
            <table>
              <thead>
                <tr>
                  {sharingInventory && <th className="w-10">Share</th>}
                  <th className="is-hoarding">
                    <Select value={filters.type || "all"} onValueChange={(v) => setFilters({ ...filters, type: v === "all" ? "" : v })}>
                      <SelectTrigger data-testid="filter-type" className="hx-table-filter">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {hoardingTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        {hoardingTypes.length === 0 && <SelectItem value="__no_types" disabled>No types yet</SelectItem>}
                      </SelectContent>
                    </Select>
                  </th>
                  <th className="is-status">
                    <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                      <SelectTrigger data-testid="filter-status" className="hx-table-filter hx-status-table-filter">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="Blocked">Blocked</SelectItem>
                        <SelectItem value="Occupied">Occupied</SelectItem>
                        <SelectItem value="Available">Available</SelectItem>
                        <SelectItem value="Maintenance">Maintenance</SelectItem>
                      </SelectContent>
                    </Select>
                  </th>
                  <th>Campaign End</th>
                  <th className="hidden lg:table-cell">Client</th>
                  <th className="hidden xl:table-cell">Size</th>
                  <th className="hidden text-right xl:table-cell">Price</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((h) => {
                  const dl = daysLeft(h.campaign_end);
                  const isEnded = dl !== null && dl < 0;
                  const isUrgent = dl !== null && dl >= 0 && dl <= 7;
                  const shareDisabled = sharingInventory && isSharedWithAllSelectedHosts(h.id);
                  return (
                    <Fragment key={h.id}>
                    <tr
                      data-testid={`hoarding-row-${h.id}`}
                      onClick={() => nav(viewingPartnerInventory ? partnerListingPath(h.id) : `/hoardings/${h.id}`)}
                      onContextMenu={(e) => openRowMenu(e, h)}
                      className={viewingPartnerInventory ? "is-partner-row" : ""}
                    >
                      {sharingInventory && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="hx-row-share-checkbox"
                            checked={selectedIds.includes(h.id)}
                            disabled={shareDisabled}
                            title={shareDisabled ? "Already shared with selected host" : undefined}
                            onChange={(e) => toggleSelected(h.id, e.target.checked, h)}
                          />
                        </td>
                      )}
                      <td>
                        <div className="hx-hoarding-cell">
                          <HoardingThumb item={h} />
                          <div className="min-w-0">
                            <strong title={h.name}>{h.name}</strong>
                            <span>
                              <b>{h.hoarding_id}</b>
                              {(h.area || h.city) && <> · {[h.area, h.city].filter(Boolean).join(", ")}</>}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="hx-status-cell"><HoardingStatusChip status={h.occupancy_status} urgent={isUrgent} compact blockedReason={h.blocked_reason} blockedUntil={h.blocked_until} /></td>
                      <td className="whitespace-nowrap">
                        {dl !== null ? (
                          <div className="hx-date-stack">
                            <strong>{fmtDate(h.campaign_end)}</strong>
                            <span className={isEnded ? "is-danger" : isUrgent ? "is-warning" : ""}>
                              {isEnded ? `Ended ${Math.abs(dl)} day${Math.abs(dl) === 1 ? "" : "s"} ago` : `${dl} day${dl === 1 ? "" : "s"} left`}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="hidden font-medium text-slate-700 whitespace-nowrap lg:table-cell">
                        {h.client_name || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="hidden xl:table-cell">
                        <span className="hx-size-cell">{h.width} x {h.height}</span>
                      </td>
                      <td className="hidden text-right hx-price-cell xl:table-cell">
                        {h.monthly_price ? inr(h.monthly_price) : "—"}
                      </td>
                    </tr>
                    {sharingInventory && selectedIds.includes(h.id) && (
                      <tr className="bg-emerald-50/30" onClick={(e) => e.stopPropagation()}>
                        <td />
                        <td colSpan={6} className="border-t-0 py-2">
                          {renderSharePriceInputs(h)}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {pageItems.length === 0 && (
                  <tr>
                    <td colSpan={sharingInventory ? 7 : 6} className="py-16 text-center">
                      <EmptyState title="No hoardings found" description="Try adjusting your filters or search query." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <PaginationFooter page={page} pageSize={pageSize} total={total} onPageChange={(p) => load(p)} />
        </section>
      )}
        </div>
      )}
      {contextMenu && (
        <div
          className="fixed z-[2100] w-56 rounded-lg border border-slate-200 bg-white p-1.5 shadow-xl shadow-slate-950/15"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
          onMouseLeave={() => setContextMenu(null)}
        >
          <div className="border-b border-slate-100 px-3 py-2">
            <div className="truncate text-xs font-bold text-slate-900">{contextMenu.item.hoarding_id}</div>
            <div className="truncate text-[11px] text-slate-500">{contextMenu.item.name}</div>
          </div>
          <div className="mt-1 space-y-0.5">
            {contextMenu.item.occupancy_status === "Blocked" && (
              <ContextMenuItem icon={CheckCircle2} label="Unblock" onClick={() => { setActionModal({ kind: "unblock", item: contextMenu.item }); setContextMenu(null); }} />
            )}
            {contextMenu.item.occupancy_status === "Available" && (
              <>
                <ContextMenuItem icon={Ban} label="Block" danger onClick={() => { setActionModal({ kind: "block", item: contextMenu.item }); setContextMenu(null); }} />
                <ContextMenuItem icon={Wrench} label="Mark Maintenance" onClick={() => { setActionModal({ kind: "maintenance", item: contextMenu.item }); setContextMenu(null); }} />
                {canCreateCampaign && <ContextMenuItem icon={CalendarDays} label="Create Campaign" onClick={() => { setContextMenu(null); nav("/campaigns/new", { state: { createCampaign: { hoarding_ids: [contextMenu.item.id] } } }); }} />}
                {canCreateCampaign && <ContextMenuItem icon={Send} label="Create Proposal" onClick={() => { setContextMenu(null); nav("/proposals/create", { state: { selected: [contextMenu.item.id] } }); }} />}
              </>
            )}
            {contextMenu.item.occupancy_status === "Maintenance" && (
              <ContextMenuItem icon={CheckCircle2} label="Mark Available" onClick={() => updateHoardingStatus(contextMenu.item, "available")} />
            )}
            <ContextMenuItem icon={Eye} label="View Details" onClick={() => { setContextMenu(null); nav(`/hoardings/${contextMenu.item.id}`); }} />
            <ContextMenuItem icon={Copy} label="Copy Hoarding ID" onClick={() => copyHoardingId(contextMenu.item)} />
          </div>
        </div>
      )}
      {actionModal?.kind === "block" && (
        <BlockHoardingModal onClose={() => setActionModal(null)} onSave={saveContextBlock} />
      )}
      {actionModal?.kind === "unblock" && (
        <UnblockHoardingModal hoarding={actionModal.item} onClose={() => setActionModal(null)} onConfirm={saveContextUnblock} />
      )}
      {actionModal?.kind === "maintenance" && (
        <MaintenanceModal onClose={() => setActionModal(null)} onSave={saveContextMaintenance} />
      )}
    </Layout>
  );
}
